/**
 * Login Page
 */

import { LocationContext } from '@/shared/types/models'
import { InputWithIcon } from '@shared/components/form'
import { PhoneInput } from '@shared/components/forms'
import { Button } from '@shared/components/ui/button'
import { Card } from '@shared/components/ui/card'
import { Checkbox } from '@shared/components/ui/checkbox'
import { useAuth } from '@shared/contexts'
import { actionStorage, credentialsStorage } from '@shared/lib/storage'
import { LogIn } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

export function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location: LocationContext = useLocation()
  const [credential, setCredential] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true) // Default to checked
  const [isLoading, setIsLoading] = useState(false)

  // Load saved credentials on mount. Phone-only: a stale 'email'-type entry
  // from before the toggle removal is discarded rather than shown.
  useEffect(() => {
    const saved = credentialsStorage.getCredentials()
    if (saved && saved.type === 'phone') {
      setCredential(saved.value)
    } else if (saved) {
      credentialsStorage.clearCredentials()
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      let cleanedCredential = credential.replace(/\D/g, '')

      // Add Argentina prefix (54 9) if it's a phone number (10 digits without country code)
      if (cleanedCredential.length === 10) {
        cleanedCredential = '549' + cleanedCredential
      }

      await login({ email: cleanedCredential, password, remember: rememberMe })
      toast.success('¡Sesión iniciada correctamente!')

      // Save or clear credentials based on remember me
      if (rememberMe) {
        credentialsStorage.setCredentials({
          type: 'phone',
          value: credential.replace(/\D/g, ''), // Store unformatted
        })
      } else {
        credentialsStorage.clearCredentials()
      }

      // Check for pending action
      const pendingAction = actionStorage.getPendingAction()

      if (pendingAction) {
        // Navigate to return path
        void navigate(pendingAction.returnPath, { replace: true })
      } else if (location.state?.from) {
        // Use stored location from navigation state
        void navigate(location.state.from, { replace: true })
      } else {
        // If user was already authenticated, go back where they came from
        // Otherwise go to home
        void navigate(location.state?.from || '/', { replace: true })
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al iniciar sesión')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGuestAccess = () => {
    void navigate('/')
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-auth bg-cover bg-center p-4">
      <Card className="w-full max-w-md p-6 shadow-sm">
        <h1 className="text-2xl font-bold text-center mb-6">Iniciar Sesión</h1>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <PhoneInput value={credential} onChange={setCredential} required />

          <InputWithIcon
            isPasswordToggle
            type="password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          <div className="flex items-center space-x-2">
            <Checkbox
              id="remember"
              checked={rememberMe}
              onCheckedChange={(c) => setRememberMe(!!c)}
            />
            <label htmlFor="remember" className="text-sm font-medium cursor-pointer">
              Recordarme
            </label>
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Iniciando sesión...' : (<><LogIn className="mr-2 h-4 w-4" />Iniciar Sesión</>)}
          </Button>

          <Button type="button" variant="outline" className="w-full" onClick={handleGuestAccess}>
            Continuar sin iniciar sesión
          </Button>

          <div className="text-center">
            <Link to="/passwordrecovery" className="text-sm text-primary hover:underline">
              ¿Olvidaste tu contraseña?
            </Link>
          </div>

          <div className="text-center text-sm text-muted-foreground">¿No tienes cuenta?</div>
          <Button variant="outline" className="w-full" asChild>
            <Link to="/register">Regístrate</Link>
          </Button>
        </form>
      </Card>
    </div>
  )
}
